<template>
  <div class="course-path-card mb-2" :class="{ 'uk-link': isProviderCard }" @click="handleClick">
    <template v-if="!isProviderCard">
      <img :src="getEnvironmentMediaUrl(training.cover_image_link || training.image)" alt="" style="width:100%">
      <div class="training-card-content">
        <div class="training-card-meta">
          <div>
            <span class="training-card-category">{{ categoryText }}</span>
          </div>
          <div v-if="training.has_present">
            <i class="icon-feather-gift" uk-tooltip="title: Hediyeli Eğitim; pos: bottom-center"></i>
          </div>
        </div>
        <p class="training-card-title">{{ training.name || training.title }}</p>
        <div class="training-card-footer">
          <div v-if="training.completion_percentage !== undefined"
            :uk-tooltip="'title: %' + training.completion_percentage + ' Tamamlandı; pos: bottom-center'"
            class="training-card-progress">
            <div :class="progressBarClass" :style="'width:' + training.completion_percentage + '%'"
              class="training-card-progress-bar"></div>
          </div>
          <p class="training-card-date">
            <i :class="dateIcon"></i>
            {{ formattedDate }}
          </p>
        </div>
      </div>
      <div class="course-path-card-footer" v-if="training.total_rating !== undefined">
        <div class="star-rating">
          <span class="avg"> {{ training.total_rating }} </span>
          <star-rating :increment="star_increment" :max-rating="5" :padding="1" :rating="training.total_rating"
            :read-only="true" :show-rating="false" :star-points="rating_star_points" :star-size="18"></star-rating>
        </div>
      </div>
    </template>

    <!-- Provider card -->
    <template v-else>
      <img :src="getEnvironmentMediaUrl(training.image || training.cover_image_link)" alt="" style="width:100%">
      <div class="training-card-content">
        <div class="training-card-meta">
          <div>
            <span class="training-card-category">{{ training.provider || 'Harvard Business' }}</span>
          </div>
        </div>
        <p class="training-card-title">{{ training.title }}</p>
      </div>
    </template>
  </div>
</template>

<script>
import StarRating from "vue-star-rating";
import moment from "moment";
import { getEnvironmentMediaUrl } from "@/utils/imageHelper";

export default {
  name: "TrainingCard",
  components: {
    StarRating
  },
  props: {
    training: {
      type: Object,
      required: true
    },
    cardType: {
      type: String,
      default: 'assigned' // assigned, highlight, popular, expired, optional, provider
    },
    rating_star_points: {
      type: Array,
      default: () => [23, 2, 14, 17, 0, 19, 10, 34, 7, 50, 23, 43, 38, 50, 36, 34, 46, 19, 31, 17]
    },
    star_increment: {
      type: Number,
      default: 0.1
    }
  },
  computed: {
    isProviderCard() {
      return this.cardType === 'provider';
    },
    activeLanguageCode() {
      return String((this.$i18n && this.$i18n.locale) || this.secureLocalStorage("language") || "tr").toLowerCase();
    },
    formattedDate() {
      const date = this.cardType === 'optional'
        ? this.training.created_at
        : this.training.end_date;

      if (!date) return '';

      const inputDate = new Date(date);
      if (Number.isNaN(inputDate.getTime())) return '';

      return moment(inputDate).locale(this.activeLanguageCode).format('DD MMMM YYYY');
    },
    routerLink() {
      if (this.cardType === 'optional') {
        return { name: 'user.education-detail', params: { id: this.training.id } };
      }
      return { name: 'user.assignment-detail', params: { id: this.training.id } };
    },
    categoryText() {
      if (this.training.is_compulsory !== undefined && this.training.is_compulsory !== null) {
        return Number(this.training.is_compulsory) === 0
          ? "İsteğe Bağlı"
          : Number(this.training.is_compulsory) === 1
            ? "Yasal Zorunlu"
            : "Zorunlu";
      }
      return '';
    },
    progressBarClass() {
      const percentage = this.training.completion_percentage;
      if (percentage >= 75) return 'bg-success';
      if (percentage >= 50) return 'bg-warning';
      return '';
    },
    dateIcon() {
      return this.cardType === 'optional' ? 'icon-feather-clock mr-1' : 'icon-feather-calendar mr-1';
    }
  },
  methods: {
    getEnvironmentMediaUrl,
    handleClick() {
      if (this.isProviderCard) {
        this.$emit('provider-click', this.training);
      } else {
        this.$router.push(this.routerLink);
      }
    }
  }
};
</script>

<style scoped>
/* Desktop — tıklanabilir kart: border yok, soft shadow */
.course-path-card {
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  border: none;
  border-radius: 14px;
  overflow: hidden;
  background: #fff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  height: 100%;
  display: flex;
  flex-direction: column;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;
}

.course-path-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
}

.course-path-card:active {
  transform: scale(0.98);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.course-path-card.uk-link {
  cursor: pointer;
}

.course-path-card img {
  width: 100%;
  height: auto;
  aspect-ratio: 16/9;
  object-fit: cover;
}

/* Training Card Content */
.training-card-content {
  padding: 14px;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.training-card-title {
  font-size: 0.85rem;
  font-weight: 600;
  line-height: 1.4;
  max-height: calc(0.85rem * 1.4 * 3);
  margin: 0;
  color: #333;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  flex: 1;
}

.training-card-meta {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.training-card-category {
  font-size: 12px;
  font-weight: 500;
  border-radius: 4px;
}

.training-card-meta .icon-feather-gift {
  font-size: 16px;
  color: #e11d48;
}

.training-card-footer {
  margin-top: auto;
}

.training-card-date {
  font-size: 12px;
  color: #666;
  margin: 0;
  display: flex;
  align-items: center;
}

.training-card-date i {
  font-size: 13px;
  margin-right: 4px;
  color: #888;
}

.training-card-progress {
  position: relative;
  height: 8px;
  border-radius: 8px;
  background: #e9ecef;
  overflow: hidden;
  margin-bottom: 10px;
}

.training-card-progress-bar {
  position: absolute;
  top: -1px;
  left: 0;
  right: -1px;
  bottom: -1px;
  height: auto;
  background-color: rgb(241, 81, 81);
  border-radius: 8px;
  transition: width 0.3s ease;
}

/* Tablet */
@media (max-width: 960px) {
  .course-path-card {
    border-radius: 14px;
  }

  /* Disable hover on touch devices */
  .course-path-card:hover {
    transform: none;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  }

  /* Active state for touch feedback */
  .course-path-card:active {
    transform: scale(0.98);
  }

  .training-card-content {
    padding: 12px;
    gap: 6px;
  }

  .training-card-title {
    font-size: 0.85rem;
  }

  .training-card-category {
    font-size: 12px;
  }

  .training-card-meta .icon-feather-gift {
    font-size: 14px;
  }

  .training-card-date {
    font-size: 12px;
  }

  .training-card-date i {
    font-size: 13px;
  }
}

/* Mobile */
@media (max-width: 768px) {
  .training-card-content {
    padding: 10px;
    gap: 5px;
  }

  .training-card-title {
    font-size: 14px;
    line-height: 1.4;
    -webkit-line-clamp: 2;
    max-height: calc(14px * 1.4 * 2);
  }

  .training-card-category {
    font-size: 12px;
  }

  .training-card-meta .icon-feather-gift {
    font-size: 13px;
  }

  .training-card-date {
    font-size: 12px;
  }

  .training-card-date i {
    font-size: 12px;
  }

  .training-card-progress {
    height: 6px;
    margin-bottom: 8px;
  }
}

/* Small Mobile */
@media (max-width: 480px) {
  .training-card-content {
    padding: 8px;
    gap: 4px;
  }

  .training-card-title {
    font-size: 13px;
    line-height: 1.4;
    -webkit-line-clamp: 2;
    max-height: calc(13px * 1.4 * 2);
  }

  .training-card-category {
    font-size: 12px;
  }

  .training-card-meta .icon-feather-gift {
    font-size: 12px;
  }

  .training-card-date {
    font-size: 12px;
  }

  .training-card-date i {
    font-size: 12px;
  }

  .training-card-progress {
    height: 5px;
    margin-bottom: 6px;
  }
}
</style>
